// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/MessageActionBottomSheet.module.css';
import {messageActionMenuItemIds, useMessageActionMenuData} from '@app/features/channel/components/MessageActionMenu';
import {MessageReactionsSheet} from '@app/features/channel/components/MessageReactionsSheet';
import {
	REACT_WITH_EMOJI_DESCRIPTOR,
	renderQuickReactionEmoji,
	useReactionMenuImagePreload,
} from '@app/features/channel/components/QuickReactionsRow';
import quickReactionStyles from '@app/features/channel/components/QuickReactionsRow.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import EmojiPicker from '@app/features/emoji/state/EmojiPicker';
import {ExpressionPickerSheet} from '@app/features/expressions/components/modals/ExpressionPickerSheet';
import {COPY_LINK_DESCRIPTOR, OPEN_LINK_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {useMessageReactions as useMessageReactionsSnapshot} from '@app/features/messaging/hooks/useMessageReactionStore';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {getEmojiNameWithColons, toReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {CopyLinkIcon, OpenLinkIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {GlassMessageMenu, GlassReaction, GlassReactionBar} from '@app/features/ui/glass_menu/GlassMessageMenu';
import {PlusIcon} from '@app/features/ui/icons/Icons';
import type {MenuGroupType, MenuSheetItem} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {MenuBottomSheet} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';

const REACT_DESCRIPTOR = msg({
	message: 'React',
	comment: 'Phone message menu: accessible name of the row of quick reaction emojis above a long-pressed message.',
});
const ADD_ANOTHER_REACTION_DESCRIPTOR = msg({
	message: 'Add another reaction',
	comment: 'Button or menu action label in the channel and chat message action bottom sheet. Keep it concise.',
});

interface MessageActionBottomSheetProps {
	isOpen: boolean;
	onClose: () => void;
	message: Message;
	sourceChannel?: Channel | null;
	handleDelete: (bypassConfirm?: boolean) => void;
	linkUrl?: string;
	/**
	 * The long-pressed message in the thread. With it the menu is the glass one (GlassMessageMenu): the
	 * message lifts above the dim, its quick reactions above it and the actions under it. Without it
	 * (a search result in a sheet) the actions stay a bottom sheet.
	 */
	anchor?: HTMLElement | null;
}

/** The lifted menu is wide enough for five quick buttons with their labels. */
const LIFTED_MENU_WIDTH = 352;
const QUICK_MAX = 5;
/** Items that open something of this menu's own (the picker, the reactions sheet): the menu stays mounted. */
const KEEPS_MENU = new Set<string>([messageActionMenuItemIds.addReaction, messageActionMenuItemIds.viewReactions]);

/** The first quick ids the groups have, as actions with an icon (as MenuBottomSheet's quick row takes them). */
function presentQuickIds(groups: ReadonlyArray<MenuGroupType>): Array<string> {
	const ids = new Set(
		groups.flatMap((group) =>
			group.items.flatMap((item: MenuSheetItem) =>
				'onClick' in item && !('items' in item) && item.id && item.icon ? [item.id] : [],
			),
		),
	);
	return MESSAGE_QUICK_IDS.filter((id) => ids.has(id)).slice(0, QUICK_MAX);
}

/**
 * Round quick actions under the reactions; the first five a message has (Ask Konvo with an
 * assistant, Agent with Agents on, Edit only on your own): Reply, Copy, Task, Ask Konvo, Agent for
 * someone else's message.
 */
const MESSAGE_QUICK_IDS = [
	messageActionMenuItemIds.reply,
	messageActionMenuItemIds.copyMessage,
	messageActionMenuItemIds.createTask,
	messageActionMenuItemIds.askKonvo,
	messageActionMenuItemIds.sendToAgent,
	messageActionMenuItemIds.edit,
	messageActionMenuItemIds.forward,
	messageActionMenuItemIds.pinMessage,
];

export const MessageActionBottomSheet: React.FC<MessageActionBottomSheetProps> = observer(
	({isOpen, onClose, message, sourceChannel, handleDelete, linkUrl, anchor}) => {
		const {i18n} = useLingui();
		const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
		const [isReactionsSheetOpen, setIsReactionsSheetOpen] = useState(false);
		const reactions = useMessageReactionsSnapshot(message.id);
		const handleOpenLink = useCallback(() => {
			if (!linkUrl) return;
			void openExternalUrl(linkUrl);
			onClose();
		}, [linkUrl, onClose]);
		const handleCopyLink = useCallback(async () => {
			if (!linkUrl) return;
			await TextCopyCommands.copy(i18n, linkUrl);
			onClose();
		}, [i18n, linkUrl, onClose]);
		const handleAddReaction = useCallback(() => {
			setIsEmojiPickerOpen(true);
		}, []);
		const handleOpenReactionsSheet = useCallback(() => {
			setIsReactionsSheetOpen(true);
		}, []);
		const handleReactionsSheetClose = useCallback(() => {
			setIsReactionsSheetOpen(false);
			onClose();
		}, [onClose]);
		const handleEmojiPickerClose = useCallback(() => {
			setIsEmojiPickerOpen(false);
			onClose();
		}, [onClose]);
		const {groups, handlers, quickReactionEmojis, quickReactionRowVisible} = useMessageActionMenuData(message, {
			onClose,
			onDelete: () => handleDelete(),
			sourceChannel,
			onOpenEmojiPicker: handleAddReaction,
			onOpenReactionsSheet: handleOpenReactionsSheet,
			// The lifted menu's pill holds six, as WhatsApp's does; the sheet's row four.
			quickReactionCount: anchor ? 6 : 4,
		});
		const linkGroups = useMemo<Array<MenuGroupType>>(() => {
			if (!linkUrl) {
				return [];
			}
			return [
				{
					items: [
						{
							id: 'open_link',
							icon: (
								<OpenLinkIcon size={20} data-flx="channel.message-action-bottom-sheet.link-groups.open-link-icon" />
							),
							label: i18n._(OPEN_LINK_DESCRIPTOR),
							onClick: handleOpenLink,
						},
						{
							id: 'copy_link',
							icon: (
								<CopyLinkIcon size={20} data-flx="channel.message-action-bottom-sheet.link-groups.copy-link-icon" />
							),
							label: i18n._(COPY_LINK_DESCRIPTOR),
							onClick: handleCopyLink,
						},
					],
				},
			];
		}, [handleCopyLink, handleOpenLink, i18n.locale, linkUrl]);
		useReactionMenuImagePreload(quickReactionEmojis);
		// The + in the reaction row opens the picker, so Add reaction leaves the list when the row shows.
		const visibleGroups = useMemo(
			() =>
				[...linkGroups, ...groups]
					.map((group) =>
						quickReactionRowVisible
							? {
									...group,
									items: group.items.filter(
										(item) => !('id' in item && item.id === messageActionMenuItemIds.addReaction),
									),
								}
							: group,
					)
					.filter((group) => group.items.length > 0),
			[groups, linkGroups, quickReactionRowVisible],
		);
		const quickReactionRow = quickReactionRowVisible ? (
			<div
				className={styles.quickReactionWrapper}
				data-flx="channel.message-action-bottom-sheet.quick-reaction-wrapper"
			>
				<div className={quickReactionStyles.row} data-flx="channel.message-action-bottom-sheet.div">
					{quickReactionEmojis.map((emoji) => {
						const emojiShortcode = getEmojiNameWithColons(toReactionEmoji(emoji));
						return (
							<button
								key={emoji.name}
								type="button"
								onClick={() => {
									EmojiPicker.trackEmoji(emoji);
									handlers.handleEmojiSelect(emoji);
									onClose();
								}}
								aria-label={i18n._(REACT_WITH_EMOJI_DESCRIPTOR, {emojiShortcode})}
								className={quickReactionStyles.button}
								data-flx="channel.message-action-bottom-sheet.button.track-emoji"
							>
								{renderQuickReactionEmoji(emoji)}
							</button>
						);
					})}
					<button
						type="button"
						onClick={handleAddReaction}
						aria-label={i18n._(ADD_ANOTHER_REACTION_DESCRIPTOR)}
						className={quickReactionStyles.button}
						data-flx="channel.message-action-bottom-sheet.button.add-reaction"
					>
						<PlusIcon size={remFromPx(24)} weight="bold" data-flx="channel.message-action-bottom-sheet.plus-icon" />
					</button>
				</div>
			</div>
		) : null;
		const liftedGroups = useMemo(
			() =>
				visibleGroups.map((group) => ({
					...group,
					items: group.items.map((item) =>
						'onClick' in item && item.id && KEEPS_MENU.has(item.id) ? {...item, closeOnSelect: false} : item,
					),
				})),
			[visibleGroups],
		);
		const reactionBar = quickReactionRowVisible ? (
			<GlassReactionBar
				label={i18n._(REACT_DESCRIPTOR)}
				moreLabel={i18n._(ADD_ANOTHER_REACTION_DESCRIPTOR)}
				onMore={handleAddReaction}
				data-flx="channel.message-action-bottom-sheet.reactions"
				moreFlx="channel.message-action-bottom-sheet.button.add-reaction"
			>
				{quickReactionEmojis.map((emoji) => {
					const emojiShortcode = getEmojiNameWithColons(toReactionEmoji(emoji));
					return (
						<GlassReaction
							key={emoji.name}
							label={i18n._(REACT_WITH_EMOJI_DESCRIPTOR, {emojiShortcode})}
							onClick={() => {
								EmojiPicker.trackEmoji(emoji);
								handlers.handleEmojiSelect(emoji);
								onClose();
							}}
							data-flx="channel.message-action-bottom-sheet.button.track-emoji"
						>
							{renderQuickReactionEmoji(emoji)}
						</GlassReaction>
					);
				})}
			</GlassReactionBar>
		) : undefined;
		const menu = anchor ? (
			isOpen &&
			!isEmojiPickerOpen &&
			!isReactionsSheetOpen && (
				<GlassMessageMenu
					bubble={anchor}
					side={anchor.dataset.bubble === 'own' ? 'end' : 'start'}
					above={reactionBar}
					groups={liftedGroups}
					quickIds={presentQuickIds(liftedGroups)}
					width={LIFTED_MENU_WIDTH}
					onClose={onClose}
					data-flx="channel.message-action-bottom-sheet.glass"
				/>
			)
		) : (
			<MenuBottomSheet
				isOpen={isOpen && !isEmojiPickerOpen && !isReactionsSheetOpen}
				onClose={onClose}
				groups={visibleGroups}
				headerContent={quickReactionRow}
				quickIds={MESSAGE_QUICK_IDS}
				quickMax={QUICK_MAX}
				data-flx="channel.message-action-bottom-sheet.menu-bottom-sheet"
			/>
		);
		return (
			<>
				{menu}
				<ExpressionPickerSheet
					isOpen={isEmojiPickerOpen}
					onClose={handleEmojiPickerClose}
					channelId={message.channelId}
					onEmojiSelect={handlers.handleEmojiSelect}
					visibleTabs={['emojis']}
					data-flx="channel.message-action-bottom-sheet.expression-picker-sheet"
				/>
				<MessageReactionsSheet
					isOpen={isReactionsSheetOpen}
					onClose={handleReactionsSheetClose}
					channelId={message.channelId}
					messageId={message.id}
					message={message}
					openToReaction={reactions[0]}
					data-flx="channel.message-action-bottom-sheet.message-reactions-sheet"
				/>
			</>
		);
	},
);
